import { ArrowLeft, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { STEP_KEYS, STEP_TITLE, type StepKey, type StepState } from "@/lib/jd-steps";
import { cn } from "@/lib/utils";

/**
 * The JD page's steps, in the order the work happens — DESIGN §20.11, redesigned 2026-10-15. It replaced
 * five equal tabs and a help box that spelled out the order; each step now carries its own state, so
 * what is left to do reads off the bar. The number's ring is green when nothing is wanted, amber when
 * something is somebody's job, grey for a setting with no right answer.
 */
export function StepsNav({
  states,
  current,
  onSelect,
}: {
  states: Record<StepKey, StepState>;
  current: StepKey;
  onSelect: (step: StepKey) => void;
}) {
  return (
    <nav
      aria-label="Steps for this job description"
      className="border-bank-border bg-bank-surface flex flex-wrap overflow-hidden rounded-lg border"
    >
      {STEP_KEYS.map((key, i) => {
        const state = states[key];
        const on = key === current;
        const side = key === "activity";
        return (
          <button
            key={key}
            type="button"
            aria-current={on ? "step" : undefined}
            onClick={() => onSelect(key)}
            className={cn(
              "border-bank-border flex min-w-0 items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors",
              "focus-visible:ring-bank-accent focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
              side ? "flex-[0_1_150px] border-l" : "flex-[1_1_170px] border-r",
              on
                ? "bg-bank-accent-wash shadow-[inset_0_-2px_0_var(--color-bank-accent)]"
                : "hover:bg-bank-surface-2",
            )}
          >
            {!side && (
              <span
                aria-hidden="true"
                className={cn(
                  "grid size-[26px] shrink-0 place-items-center rounded-full border-2 font-mono text-xs font-semibold",
                  state.tone === "ok" && "border-bank-good text-bank-good-ink bg-bank-surface",
                  state.tone === "warn" &&
                    "border-bank-attention text-bank-attention-ink bg-bank-attention-wash",
                  state.tone === "plain" && "border-bank-border-strong text-bank-muted bg-bank-surface",
                )}
              >
                {i + 1}
              </span>
            )}
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="text-[13.5px] font-semibold">{STEP_TITLE[key]}</span>
              <span
                className={cn(
                  "truncate text-xs",
                  state.tone === "ok" && !side && "text-bank-good-ink",
                  state.tone === "warn" && "text-bank-attention-ink",
                  (state.tone === "plain" || side) && "text-bank-muted",
                )}
              >
                {state.line}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/** Back and Next under each step, so the order the bar shows is also the order the page walks. */
export function StepFooter({ current, onSelect }: { current: StepKey; onSelect: (step: StepKey) => void }) {
  const steps: StepKey[] = STEP_KEYS.filter((k) => k !== "activity");
  const at = steps.indexOf(current);
  if (at < 0) return null;
  const back = steps[at - 1];
  const next = steps[at + 1];
  return (
    <div className="flex justify-between gap-2 pt-1 pb-2">
      {back ? (
        <Button variant="ghost" onClick={() => onSelect(back)}>
          <ArrowLeft className="size-4" aria-hidden="true" />
          {STEP_TITLE[back]}
        </Button>
      ) : (
        <span />
      )}
      {next && (
        <Button onClick={() => onSelect(next)}>
          Next: {STEP_TITLE[next]}
          <ArrowRight className="size-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}
